import React, { useState } from 'react';
import { Mail, Phone, Linkedin, ExternalLink, Send, CheckCircle } from 'lucide-react';
import './Contact.css';
import { professorData } from '../utils/mockData';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    role: '',
    engagementType: '',
    message: ''
  });
  const [submitStatus, setSubmitStatus] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock form submission
    setSubmitStatus('success');
    setFormData({
      name: '',
      email: '',
      organization: '',
      role: '',
      engagementType: '',
      message: ''
    });
    
    setTimeout(() => setSubmitStatus(''), 5000);
  };

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="container">
          <div className="contact-hero-content">
            <h1>Get in Touch</h1>
            <p className="contact-hero-subtitle">
              For strategic advisory inquiries, speaking engagements, or research collaborations
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="contact-content-section section">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Information */}
            <div className="contact-info">
              <div className="contact-info-block">
                <h2>Let's Discuss Your Challenge</h2>
                <p className="contact-info-text">
                  Whether you're navigating organizational transformation, evaluating policy interventions, 
                  or seeking strategic clarity at an inflection point, I welcome conversations with leaders 
                  facing complex challenges.
                </p>
                <p className="contact-info-text">
                  Initial conversations are exploratory—not sales pitches. You'll leave with valuable 
                  perspective regardless of whether we work together.
                </p>
              </div>

              <div className="contact-methods">
                <h3>Direct Contact</h3>
                <div className="contact-method">
                  <div className="method-icon">
                    <Mail size={24} />
                  </div>
                  <div className="method-details">
                    <div className="method-label">Email</div>
                    <a href={`mailto:${professorData.email}`} className="method-value">
                      {professorData.email}
                    </a>
                  </div>
                </div>

                <div className="contact-method">
                  <div className="method-icon">
                    <Phone size={24} />
                  </div>
                  <div className="method-details">
                    <div className="method-label">Phone</div>
                    <a href={`tel:${professorData.phone}`} className="method-value">
                      {professorData.phone}
                    </a>
                  </div>
                </div>

                <div className="contact-method">
                  <div className="method-icon">
                    <Linkedin size={24} />
                  </div>
                  <div className="method-details">
                    <div className="method-label">LinkedIn</div>
                    <a 
                      href={professorData.linkedIn} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="method-value"
                    >
                      Connect on LinkedIn
                      <ExternalLink size={16} />
                    </a>
                  </div>
                </div>
              </div>

              <div className="calendar-info">
                <h3>Book Strategy Session</h3>
                <p>
                  Schedule a 30-minute strategic conversation to discuss your challenge. 
                  Select a convenient time that works for you.
                </p>
                <div className="calendly-embed">
                  <iframe
                    src={professorData.calendlyLink}
                    width="100%"
                    height="700"
                    frameBorder="0"
                    title="Book Strategy Session"
                  ></iframe>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="contact-form-container">
              <div className="form-header">
                <h2>Send Strategic Inquiry</h2>
                <p>Share details about your challenge, and I'll respond within 48 hours.</p>
              </div>

              {submitStatus === 'success' && (
                <div className="success-message">
                  <CheckCircle size={24} />
                  <div>
                    <strong>Message sent successfully!</strong>
                    <p>I'll review your inquiry and respond within 48 hours.</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="form-input"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="organization">Organization</label>
                    <input
                      type="text"
                      id="organization"
                      name="organization"
                      value={formData.organization}
                      onChange={handleChange}
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="role">Your Role</label>
                    <input
                      type="text"
                      id="role"
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      className="form-input"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="engagementType">Type of Engagement</label>
                  <select
                    id="engagementType"
                    name="engagementType"
                    value={formData.engagementType}
                    onChange={handleChange}
                    className="form-select"
                  >
                    <option value="">Select engagement type</option>
                    <option value="strategic-growth">Strategic Growth Advisory</option>
                    <option value="problem-solving">Complex Problem Solving</option>
                    <option value="government">Government & Institutional Advisory</option>
                    <option value="executive-mentorship">Executive Mentorship</option>
                    <option value="venture-strategy">Venture & Enterprise Strategy</option>
                    <option value="speaking">Speaking Engagement</option>
                    <option value="research">Research Collaboration</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message">Describe Your Strategic Challenge *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    className="form-textarea"
                    placeholder="Please share context about your challenge, organizational situation, and what you hope to achieve through strategic advisory..."
                  ></textarea>
                </div>

                <button type="submit" className="btn btn-primary btn-large btn-submit">
                  <Send size={20} />
                  Send Strategic Inquiry
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Who Should Reach Out Section */}
      <section className="who-section section-light">
        <div className="container">
          <div className="section-header">
            <h2>Who Should Reach Out</h2>
          </div>
          <div className="who-grid">
            <div className="who-card">
              <h3>You're Facing Genuine Complexity</h3>
              <p>
                Your challenge resists standard solutions. Multiple stakeholders, conflicting objectives, 
                and systemic constraints make this more than a tactical problem.
              </p>
            </div>
            <div className="who-card">
              <h3>Decisions Have Significant Stakes</h3>
              <p>
                The choices you're making will shape your organization's trajectory for years. You need 
                strategic clarity, not quick fixes.
              </p>
            </div>
            <div className="who-card">
              <h3>You Value Evidence Over Opinion</h3>
              <p>
                You want recommendations grounded in rigorous research and proven frameworks, not gut 
                feelings or trendy methodologies.
              </p>
            </div>
            <div className="who-card">
              <h3>You're Ready for Honest Assessment</h3>
              <p>
                You want intellectual honesty—someone who will challenge assumptions and tell you when 
                conventional wisdom might be leading you astray.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="location-section section">
        <div className="container">
          <div className="location-content">
            <h2>Based in London, Operating Globally</h2>
            <p className="location-text">
              While based at Kingston University London, I work with clients worldwide through 
              in-person consultations, virtual sessions, and hybrid engagement models. Strategic 
              advisory transcends geography when the challenge demands it.
            </p>
            <div className="location-details">
              <div className="location-detail">
                <strong>Primary Location:</strong>
                <span>Kingston Business School, London, United Kingdom</span>
              </div>
              <div className="location-detail">
                <strong>Advisory Reach:</strong>
                <span>Europe | Americas | Asia | Africa</span>
              </div>
              <div className="location-detail">
                <strong>Engagement Modes:</strong>
                <span>In-person | Virtual | Hybrid</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;